<template>
  <div>
    <v-card class="top-row py-2 px-3" elevation="2">
      <div class="left">
        <h3>Sökningar</h3>
      </div>
      <div class="right">
        <v-tooltip bottom v-if="!hasDateFilter">
          <template v-slot:activator="{ on, attrs }">
            <span v-on="on" v-bind="attrs">
              <v-btn size="large" color="primary" disabled>
                Exportera
              </v-btn>
            </span>
          </template>
          <span>Välj ett datumintervall för att exportera</span>
        </v-tooltip>
        <v-btn v-else size="large" color="primary" :to="{ name: 'SearchLogsExport' }">
          Exportera
        </v-btn>
      </div>
    </v-card>

    <v-card class="top-row py-2 px-3 mt-3" elevation="2">
      <div class="date-filters">
        <DatePicker
          label="Från datum"
          :date="fromDate"
          @change="onFromDateChange"
        />
        <DatePicker
          label="Till datum"
          :date="toDate"
          @change="onToDateChange"
        />
        <div class="preset-btns">
          <v-btn small outlined color="primary" @click="setPreset(7)">
            Senaste 7 dagarna
          </v-btn>
          <v-btn small outlined color="primary" @click="setPreset(30)">
            Senaste 30 dagarna
          </v-btn>
          <v-btn small text @click="clearDates" v-if="hasDateFilter">
            Rensa datum
          </v-btn>
        </div>
      </div>
    </v-card>

    <v-card :loading="loading" class="mt-3" elevation="2">
      <div class="list">
        <div class="list-item bold">
          <div>Sökfråga</div>
          <div>Innehållstyp</div>
          <div>Status</div>
          <div>Fel</div>
          <div>Skapad</div>
        </div>
        <div v-for="item in items" :key="item.id" class="list-item">
          <div>{{ item.user_query }}</div>
          <div>{{ item.content_type }}</div>
          <div>
            <v-chip small :color="item.success ? 'success' : 'error'" text-color="white">
              {{ item.success ? 'Lyckades' : 'Misslyckades' }}
            </v-chip>
          </div>
          <div>{{ item.error || '-' }}</div>
          <div>{{ formatDate(item.created_at) }}</div>
        </div>
      </div>

      <div class="pagination-container pa-3">
        <Pagination v-bind="meta" @change="onPagination" show-total />
      </div>
    </v-card>

    <router-view />
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex'
import Pagination from '@/components/shared/Pagination.vue'
import DatePicker from '@/components/shared/DatePicker.vue'
import { dateParse } from '@/lib/misc'
import { subDays, format } from 'date-fns'

export default {
  name: 'SearchLogsHome',
  components: {
    Pagination,
    DatePicker,
  },
  data() {
    return {
      loading: true,
      fromDate: '',
      toDate: '',
    }
  },
  computed: {
    ...mapGetters('searchLogs', { items: 'getArray', meta: 'getMeta' }),
    hasDateFilter() {
      return !!this.fromDate || !!this.toDate
    },
  },
  methods: {
    ...mapMutations('searchLogs', {
      addFilter: 'ADD_FILTER',
      setSort: 'SET_SORT',
      clearFilters: 'CLEAR_FILTERS',
    }),
    ...mapActions('searchLogs', { fetchCollection: 'readCollection' }),
    formatDate(date) {
      return date ? dateParse(date) : ''
    },
    onPagination(val) {
      this.addFilter({ key: 'pageNumber', value: val })
      this.fetch()
    },
    onFromDateChange(date) {
      this.fromDate = date
      this.applyDateFilters()
    },
    onToDateChange(date) {
      this.toDate = date
      this.applyDateFilters()
    },
    setPreset(days) {
      const today = format(new Date(), 'yyyy-MM-dd')
      const past = format(subDays(new Date(), days), 'yyyy-MM-dd')
      this.fromDate = past
      this.toDate = today
      this.applyDateFilters()
    },
    clearDates() {
      this.fromDate = ''
      this.toDate = ''
      this.addFilter({ key: 'pageSize', value: 50 })
      this.addFilter({ key: 'pageNumber', value: 1 })
      this.addFilter({ key: 'fromDate', value: '' })
      this.addFilter({ key: 'toDate', value: '' })
      this.fetch()
    },
    applyDateFilters() {
      this.addFilter({ key: 'pageSize', value: 50 })
      this.addFilter({ key: 'pageNumber', value: 1 })
      this.addFilter({ key: 'fromDate', value: this.fromDate })
      this.addFilter({ key: 'toDate', value: this.toDate })
      this.fetch()
    },
    async fetch() {
      this.loading = true
      await this.fetchCollection()
      this.loading = false
    },
  },
  async created() {
    this.addFilter({ key: 'pageSize', value: 50 })
    this.setSort({ key: 'created_at', value: 'DESC' })
    await this.fetch()
  },
}
</script>

<style lang="less" scoped>
.top-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  .left {
    display: flex;
    align-items: center;
  }
  .right {
    display: flex;
    align-items: center;
    gap: 10px;
  }
}

.date-filters {
  display: flex;
  align-items: center;
  gap: 15px;
  flex-wrap: wrap;

  .preset-btns {
    display: flex;
    gap: 8px;
  }
}

.list {
  font-size: 0.9rem;
  overflow: auto;

  .list-item {
    display: grid;
    grid-template-columns: 2fr 1fr 100px 1fr 150px;
    grid-column-gap: 10px;
    padding: 6px 10px;
    border-bottom: #ccc 1px solid;
    background-color: #fff;
    align-items: center;
  }

  .bold {
    font-weight: 500;
    background-color: #f5f5f5;
  }
}

.pagination-container {
  display: flex;
  justify-content: flex-end;
}
</style>
